/**
 * tile-codec.ts — 统一 tile 编码映射（单一真相源）
 *
 * 消除 5 处重复的 char/digit/enum 转换：
 *   - map-utils.ts charToTileType
 *   - level-storage-middleware.ts CHAR_TO_TILE / TILE_TO_CHAR
 *   - screen-4-battle.ts numToTile / charToTile
 *   - screen-7-map-editor.ts TILE_EMPTY/COVER/OBSTACLE/UNREACHABLE 常量
 *
 * 三套编码：
 *   1. char  (磁盘格式 string[])  : . C # S X
 *   2. digit (客户端格式 number[][]) : 0 1 2 3 (无 spawn)
 *   3. enum  (TS TileType)          : "empty" "cover" "obstacle" "spawn" "unreachable"
 *
 * 注意：digit 格式不含 spawn。middleware diskToClientMap 将 S→0(empty)，
 *   因为 spawn 不阻挡移动/视野（功能等价于 empty，见 battle_easy.json terrain_definitions）。
 */

import type { TileType } from "./types";

// ===== char ↔ TileType (enum) — map-utils / screen-3 / screen-4 使用 =====

export const CHAR_TO_TILE: Record<string, TileType> = {
  ".": "empty",
  "C": "cover",
  "#": "obstacle",
  "S": "spawn",
  "X": "unreachable",
};

export const TILE_TO_CHAR: Record<TileType, string> = {
  "empty": ".",
  "cover": "C",
  "obstacle": "#",
  "spawn": "S",
  "unreachable": "X",
};

// ===== number ↔ TileType (LevelStore client format, 无 spawn) =====

export const NUM_TO_TILE: Record<number, TileType> = {
  0: "empty",
  1: "cover",
  2: "obstacle",
  3: "unreachable",
};

export const TILE_TO_NUM: Partial<Record<TileType, number>> = {
  "empty": 0,
  "cover": 1,
  "obstacle": 2,
  "unreachable": 3,
};

// ===== char ↔ number (middleware disk 格式, 含 lowercase + O/U 别名) =====
// 与原 middleware CHAR_TO_TILE 完全对齐：S 不在此表中（middleware 在 diskToClientMap
// 中先将 S 替换为 . 再转换，或通过 default→0 处理）

export const CHAR_TO_NUM: Record<string, number> = {
  ".": 0, "C": 1, "c": 1,
  "#": 2, "O": 2, "o": 2,
  "X": 3, "U": 3, "u": 3,
};

export const NUM_TO_CHAR: Record<number, string> = {
  0: ".", 1: "C", 2: "#", 3: "X",
};

// ===== 数字常量（screen-7-map-editor 等直接用数字比较的场景） =====

export const TILE_NUM = {
  EMPTY: 0,
  COVER: 1,
  OBSTACLE: 2,
  UNREACHABLE: 3,
} as const;
